import { targetMatches, targetSpecificity } from "src/app/domains/employee-target";
import {
  CONTRACT_HOURS_SLOT_TYPES,
  PlanningPredefinedTimes,
  PredefinedTimesPeriod,
  PredefinedTimesPeriodMode,
  TimeSlot,
} from "src/app/domains/planning-predefined-times";

import { HoursPerDay } from "./hours-per-day";

const MINUTES_PER_HOUR = 60;
const MS_PER_DAY = 24 * MINUTES_PER_HOUR * 60 * 1000;
const DAYS_IN_A_YEAR = 365;
/** Année non bissextile de référence, pour comparer des durées récurrentes stables. */
const REFERENCE_YEAR = 2001;
/** Plausibilité du facteur mensuel d'une position : 52/12 = 4.33, forfaits CCT autour. */
const MONTHLY_FACTOR_MIN = 3.5;
const MONTHLY_FACTOR_MAX = 5.5;

export class ContractHours {
  contract_hours_id: string;
  predefined_times_id: string = null;
  monthly_hours: number = 0;
  period: PredefinedTimesPeriod = new PredefinedTimesPeriod();
  weekly_hours: number = 0;
  weekly_days: number = 5;
  daily_hours: HoursPerDay[] = [];
  /** Servie par l'API, jamais stockée : refusée par le back en écriture. Pour s'en écarter, on
   * crée une entrée saisie qui la surcharge. */
  derived = false;

  constructor() {
    this.contract_hours_id = generateObjectId();
    this.predefined_times_id = null;
    this.period = new PredefinedTimesPeriod();
    this.daily_hours = [];
  }

  public static createInstance(jsonData: ContractHours): ContractHours {
    const c = new ContractHours();
    if (!jsonData) {
      return c;
    }

    c.contract_hours_id = jsonData.contract_hours_id || generateObjectId();
    c.predefined_times_id = jsonData.predefined_times_id || null;
    // Aucun défaut ici : le back stocke tel quel, un `?? 0` inventerait des heures.
    c.monthly_hours = jsonData.monthly_hours ?? null;
    c.weekly_hours = jsonData.weekly_hours ?? null;
    c.weekly_days = jsonData.weekly_days ?? null;
    c.period = jsonData.period ? PredefinedTimesPeriod.createInstance(jsonData.period) : new PredefinedTimesPeriod();
    c.daily_hours = (jsonData.daily_hours || []).map((d) => createHoursPerDay(d));
    c.derived = !!jsonData.derived;

    return c;
  }

  public static createInstances(jsonData: ContractHours[]): ContractHours[] {
    return jsonData.map(ContractHours.createInstance);
  }
}

/** Heures effectives d'une position à une date donnée, tous liens de plage déjà résolus. */
export interface ResolvedContractHours {
  weekly_hours: number;
  weekly_days: number;
  monthly_hours: number;
  daily_hours: HoursPerDay[];
  predefined_times_id: string;
  /** "" pour une entrée autonome, sinon le nom de la plage horaire liée. */
  source_label: string;
}

/** Heures d'une semaine type dérivées des créneaux d'une plage horaire. */
export interface DerivedPredefinedTimesHours {
  daily_hours: HoursPerDay[];
  weekly_days: number;
  weekly_hours: number;
}

/** Champs d'une position nécessaires à la résolution. */
export interface ContractHoursPosition {
  position_start_date: Date;
  position_end_date: Date;
  weekly_hours: number;
  weekly_days: number;
  monthly_hours: number;
  daily_hours: HoursPerDay[];
  contract_hours?: ContractHours[];
}

/** Une entrée résolue vers ses champs effectifs, avant départage des candidates. */
export interface ResolvedContractHoursEntry {
  period: PredefinedTimesPeriod;
  daily_hours: HoursPerDay[];
  weekly_days: number;
  weekly_hours: number;
  source_label: string;
}

export function buildPredefinedTimesMap(
  predefinedTimes: PlanningPredefinedTimes[],
): Map<string, PlanningPredefinedTimes> {
  return new Map((predefinedTimes || []).map((pt) => [pt.predefined_times_id, pt]));
}

export function isDayInPeriod(period: PredefinedTimesPeriod, day: Date): boolean {
  if (!period || period.mode === PredefinedTimesPeriodMode.NONE) {
    return true;
  }
  if (!period.start || !period.end) {
    // Sans ce refus, une période restrictive malformée deviendrait une candidate de durée
    // infinie, capable de supplanter le socle.
    return false;
  }

  const clampedDay = clampLeapDay(day);

  if (period.mode === PredefinedTimesPeriodMode.LIMITED) {
    return (
      atMidnight(clampedDay) >= boundAtMidnight(period.start) && atMidnight(clampedDay) <= boundAtMidnight(period.end)
    );
  }

  const startOrdinal = boundMonthDayOrdinal(period.start);
  const endOrdinal = boundMonthDayOrdinal(period.end);
  const dayOrdinal = monthDayOrdinal(clampedDay);

  if (Number.isNaN(startOrdinal) || Number.isNaN(endOrdinal)) {
    // Une borne invalide ne doit jamais faire gagner la branche "bornes inversées" ci-dessous.
    return false;
  }

  if (startOrdinal <= endOrdinal) {
    return dayOrdinal >= startOrdinal && dayOrdinal <= endOrdinal;
  }

  // Bornes inversées : la période chevauche le nouvel an (ex. 15.11 → 15.02).
  return dayOrdinal >= startOrdinal || dayOrdinal <= endOrdinal;
}

export function periodDurationDays(period: PredefinedTimesPeriod): number {
  if (!period || period.mode === PredefinedTimesPeriodMode.NONE || !period.start || !period.end) {
    return Infinity;
  }

  if (period.mode === PredefinedTimesPeriodMode.LIMITED) {
    return daysBetween(boundAtMidnight(period.start), boundAtMidnight(period.end)) + 1;
  }

  const start = monthDayOnReferenceYear(period.start);
  const end = monthDayOnReferenceYear(period.end);

  if (start <= end) {
    return daysBetween(start, end) + 1;
  }

  return DAYS_IN_A_YEAR - daysBetween(end, start) + 1;
}

/** Sur la seule heure locale des bornes : la date qu'elles portent n'a aucun sens, le
 * formulaire ne réécrit que l'heure de la borne touchée. */
function slotDurationHours(slot: TimeSlot): number {
  const start = new Date(slot.start);
  const end = new Date(slot.end);
  const startMinutes = start.getHours() * MINUTES_PER_HOUR + start.getMinutes();
  const endMinutes = end.getHours() * MINUTES_PER_HOUR + end.getMinutes();

  return Math.max(0, endMinutes - startMinutes) / MINUTES_PER_HOUR;
}

export function deriveFromPredefinedTimes(predefinedTimes: PlanningPredefinedTimes): DerivedPredefinedTimesHours {
  const daily_hours = defaultDailyHours();

  for (const times of predefinedTimes?.times || []) {
    for (const dayIndex of times.days || []) {
      if (dayIndex < 0 || dayIndex > 6) {
        continue;
      }

      let hours = 0;
      for (const slot of times.time_slots || []) {
        if (!CONTRACT_HOURS_SLOT_TYPES.includes(slot.type) || !slot.start || !slot.end) {
          continue;
        }

        hours += slotDurationHours(slot);
      }

      // Un même jour peut apparaître dans plusieurs `times` : on cumule plutôt que d'écraser.
      daily_hours[dayIndex].hours += hours;
    }
  }

  const weekly_days = daily_hours.filter((d) => d.hours > 0).length;
  const weekly_hours = roundToTwoDecimals(daily_hours.reduce((total, d) => total + d.hours, 0));

  return { daily_hours, weekly_days, weekly_hours };
}

/** Synthétise l'entrée socle (période NONE) depuis les champs plats d'une position. */
export function buildSocleFromFlatFields(position: ContractHoursPosition): ContractHours {
  const socle = new ContractHours();
  socle.weekly_hours = position.weekly_hours;
  socle.weekly_days = position.weekly_days;
  socle.monthly_hours = position.monthly_hours;
  // Copie profonde : sans elle, muter l'un muterait l'autre.
  socle.daily_hours = (position.daily_hours || []).map((d) => createHoursPerDay(d));
  socle.predefined_times_id = null;

  return socle;
}

function socleEntry(position: ContractHoursPosition): ContractHours {
  // Une entrée liée porte elle aussi `mode === NONE` : sans l'exclure, elle passerait pour le socle.
  return (
    (position.contract_hours || []).find(
      (entry) => entry.period?.mode === PredefinedTimesPeriodMode.NONE && !entry.predefined_times_id,
    ) ?? buildSocleFromFlatFields(position)
  );
}

function derivedMonthlyHours(
  predefinedTimes: PlanningPredefinedTimes,
  socle: ContractHours,
  weeklyHours: number,
): number {
  if (predefinedTimes?.monthly_hours) {
    return predefinedTimes.monthly_hours;
  }
  if (!weeklyHours || !socle?.weekly_hours || !socle?.monthly_hours) {
    return null;
  }

  const factor = socle.monthly_hours / socle.weekly_hours;
  // Hors bornes, le facteur en place est une saisie fausse : mieux vaut rien que la propager.
  if (factor < MONTHLY_FACTOR_MIN || factor > MONTHLY_FACTOR_MAX) {
    return null;
  }

  return roundHalfToEvenTwoDecimals(weeklyHours * factor);
}

/** Horaires d'entreprise d'une position, miroir de `derived_contract_hours` : à recalculer pour
 * une position que l'API n'a jamais servie (création, ajout). */
export function derivedContractHours(
  position: ContractHoursPosition,
  employeeId: string,
  employeeType: string,
  predefinedTimes: PlanningPredefinedTimes[],
): ContractHours[] {
  // Le facteur de conversion se lit sur le socle, pas sur les champs plats : ceux-ci sont réécrits
  // par l'horaire applicable, donc par une dérivée, et le recalcul suivant repartirait de là.
  const socle = socleEntry(position);
  const halfDayByDay = new Map((socle.daily_hours || []).map((d) => [d.day, d.half_day]));

  return (
    (predefinedTimes || [])
      // Pas de filtre sur le statut : il ne pilote que le planning, la période borne les contrats.
      .filter((pt) => pt.defines_contract_hours && targetMatches(pt.target, employeeId, employeeType))
      .map((pt) => ({ predefinedTimes: pt, heures: deriveFromPredefinedTimes(pt) }))
      // Sans heure ACTIVITY, la plage écraserait le socle de tous ses ciblés à zéro.
      .filter(({ heures }) => !!heures.weekly_hours)
      .map(({ predefinedTimes: pt, heures }) => {
        const entry = new ContractHours();
        // Même clé que celle servie par l'API, sinon la ligne se recrée à chaque rendu.
        entry.contract_hours_id = pt.predefined_times_id;
        entry.predefined_times_id = pt.predefined_times_id;
        entry.derived = true;
        // `new PredefinedTimesPeriod()` date ses bornes à maintenant : deux appels rendraient des
        // entrées différentes là où le back en rend d'identiques.
        entry.period.start = null;
        entry.period.end = null;
        // `resolveEntry` relit le rythme sur la plage : le recopier ici divergerait dès sa 1re modif.
        entry.weekly_hours = null;
        entry.weekly_days = null;
        entry.daily_hours = defaultDailyHours().map((day) => ({
          ...day,
          half_day: halfDayByDay.get(day.day) ?? false,
        }));
        entry.monthly_hours = derivedMonthlyHours(pt, socle, heures.weekly_hours);

        return entry;
      })
  );
}

/** Identifiants régénérés : deux positions ne doivent jamais porter des entrées indiscernables.
 * Les entrées dérivées sont écartées : l'API les re-sert depuis la plage, les dupliquer figerait
 * une copie qui ne suivrait plus ses mises à jour. */
export function duplicateContractHours(entries: ContractHours[]): ContractHours[] {
  return (entries || [])
    .filter((entry) => !entry.derived)
    .map((entry) => {
      const copy = ContractHours.createInstance(entry);
      copy.contract_hours_id = generateObjectId();

      return copy;
    });
}

/** Une plage ne porte pas la demi-journée : sans ce report, une absence serait comptée à la
 * moitié des heures dues. */
function applyHalfDayFromEntry(dailyHours: HoursPerDay[], entry: ContractHours): HoursPerDay[] {
  const halfDayByDay = new Map((entry.daily_hours || []).map((d) => [d.day, d.half_day]));
  for (const day of dailyHours) {
    day.half_day = halfDayByDay.get(day.day) ?? false;
  }

  return dailyHours;
}

/** Une entrée liée tient sa période et ses heures de la plage, même désactivée ; une entrée dont
 * la plage est introuvable n'est pas résolvable. */
export function resolveEntry(
  entry: ContractHours,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
): ResolvedContractHoursEntry {
  if (entry.predefined_times_id) {
    const predefinedTimes = predefinedTimesById?.get(entry.predefined_times_id);
    if (!predefinedTimes) {
      return null;
    }

    const derived = deriveFromPredefinedTimes(predefinedTimes);

    return {
      period: predefinedTimes.period,
      daily_hours: applyHalfDayFromEntry(derived.daily_hours, entry),
      weekly_days: derived.weekly_days,
      weekly_hours: derived.weekly_hours,
      source_label: predefinedTimes.name,
    };
  }

  return {
    period: entry.period,
    // Copie : sans elle, muter le résultat muterait la position en mémoire.
    daily_hours: (entry.daily_hours || []).map((d) => createHoursPerDay(d)),
    weekly_days: entry.weekly_days,
    weekly_hours: entry.weekly_hours,
    source_label: "",
  };
}

/** Options de résolution, miroir des paramètres nommés du résolveur back. */
export interface ResolveContractHoursOptions {
  /** Résout même un jour hors des bornes de la position. Réservé aux vues semaine et aux
   * compteurs, qui choisissent une position une fois pour plusieurs jours ; ces heures
   * alimentent des soldes déjà affichés. */
  ignorePositionBounds?: boolean;
}

/** `null` hors des bornes de la position, sinon l'entrée applicable qui gagne le départage. */
export function resolveContractHours(
  position: ContractHoursPosition,
  day: Date,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
  options: ResolveContractHoursOptions = {},
): ResolvedContractHours {
  if (!position || !day) {
    return null;
  }

  if (!options.ignorePositionBounds) {
    const dayValue = atMidnight(day);
    if (position.position_start_date && dayValue < atMidnight(position.position_start_date)) {
      return null;
    }
    if (position.position_end_date && dayValue > atMidnight(position.position_end_date)) {
      return null;
    }
  }

  // Une position échappée au backfill porte ses heures dans ses champs plats.
  const entries = position.contract_hours?.length ? position.contract_hours : [buildSocleFromFlatFields(position)];

  const candidates: { duration: number; specificity: number; resolved: ResolvedContractHours }[] = [];
  for (const entry of entries) {
    const resolvedEntry = resolveEntry(entry, predefinedTimesById);
    if (!resolvedEntry) {
      continue;
    }
    if (!isDayInPeriod(resolvedEntry.period, day)) {
      continue;
    }

    candidates.push({
      duration: periodDurationDays(resolvedEntry.period),
      specificity: entrySpecificity(entry, predefinedTimesById),
      resolved: {
        weekly_hours: resolvedEntry.weekly_hours,
        weekly_days: resolvedEntry.weekly_days,
        monthly_hours: entry.monthly_hours,
        daily_hours: resolvedEntry.daily_hours,
        predefined_times_id: entry.predefined_times_id,
        source_label: resolvedEntry.source_label,
      },
    });
  }

  if (!candidates.length) {
    return null;
  }

  // Départage identique au back (`contract_hours_effective`) : les deux doivent désigner la même
  // entrée, sinon l'écran affiche des heures et les compteurs en utilisent d'autres.
  return candidates.reduce((best, candidate) =>
    candidate.duration < best.duration ||
    (candidate.duration === best.duration && candidate.specificity < best.specificity)
      ? candidate
      : best,
  ).resolved;
}

/** Miroir de `entry_specificity` : une entrée saisie vaut 0, une dérivée hérite de la précision
 * de la cible de sa plage. */
function entrySpecificity(entry: ContractHours, predefinedTimesById: Map<string, PlanningPredefinedTimes>): number {
  if (!entry.derived) {
    return 0;
  }
  const predefinedTimes = predefinedTimesById.get(entry.predefined_times_id);
  if (!predefinedTimes) {
    return 0;
  }

  return targetSpecificity(predefinedTimes.target);
}

/** Heures dues et statut demi-journée pour ce jour précis (`daily_hours` est indexé 0 = lundi). */
export function dailyHoursForDay(resolved: ResolvedContractHours, day: Date): { hours: number; half_day: boolean } {
  if (!resolved) {
    return { hours: 0, half_day: false };
  }

  const weekDay = mondayBasedWeekDay(day);
  const entry = (resolved.daily_hours || []).find((d) => d.day === weekDay);

  return entry ? { hours: entry.hours, half_day: entry.half_day } : { hours: 0, half_day: false };
}

/** Chaque jour se résout sur sa propre date. */
export function resolvePositionDailyHours(
  position: ContractHoursPosition,
  day: Date,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
  options: ResolveContractHoursOptions = {},
): { hours: number; half_day: boolean } {
  return dailyHoursForDay(resolveContractHours(position, day, predefinedTimesById, options), day);
}

/** Heures dues et demi-journées des 7 jours d'une semaine, indexées lundi -> dimanche. */
export function resolveWeekPositionDailyHours(
  position: ContractHoursPosition,
  weekStart: Date,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
  options: ResolveContractHoursOptions = {},
): { workingHoursForDays: number[]; areHalfDays: boolean[] } {
  const workingHoursForDays: number[] = [];
  const areHalfDays: boolean[] = [];
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + dayIndex);
    const resolved = resolvePositionDailyHours(position, day, predefinedTimesById, options);
    workingHoursForDays.push(resolved.hours);
    areHalfDays.push(resolved.half_day);
  }
  return { workingHoursForDays, areHalfDays };
}

const FAR_FUTURE_DATE = new Date(9999, 11, 31);

function dateAtMidnight(date: Date): Date {
  const clamped = new Date(date);
  clamped.setHours(0, 0, 0, 0);

  return clamped;
}

/** Bornes de la position à minuit, fin ouverte si absente ou si elle précède le début : le domaine
 * traduit une fin nulle en 1970-01-01 (`new Date(null)`), qui ne doit jamais fermer la fenêtre. */
function positionBounds(position: ContractHoursPosition): { start: Date; end: Date } | null {
  if (!position.position_start_date) {
    return null;
  }
  const start = dateAtMidnight(position.position_start_date);
  const rawEnd = position.position_end_date ? dateAtMidnight(position.position_end_date) : FAR_FUTURE_DATE;
  const end = rawEnd < start ? FAR_FUTURE_DATE : rawEnd;

  return { start, end };
}

function clampToCivilYear(bounds: { start: Date; end: Date }, today: Date): { start: Date; end: Date } | null {
  const yearStart = new Date(today.getFullYear(), 0, 1);
  const yearEnd = new Date(today.getFullYear(), 11, 31);
  const windowStart = bounds.start > yearStart ? bounds.start : yearStart;
  const windowEnd = bounds.end < yearEnd ? bounds.end : yearEnd;

  return windowStart > windowEnd ? null : { start: windowStart, end: windowEnd };
}

/** Fenêtre de pondération du diviseur mensuel, miroir de `compute_weighted_average_monthly_hours`
 * côté back : année civile de `today` écrêtée aux bornes de la position, ou durée réelle de la
 * position si elle fait moins d'un an (sinon une position courte hors de l'année en cours rendrait
 * une fenêtre vide). Réservée à `weightedMonthlyHours` : `weightedWeeklyHours` a sa propre fenêtre,
 * qui ne s'élargit jamais à la durée de la position. */
function weightingWindow(position: ContractHoursPosition, today: Date): { start: Date; end: Date } | null {
  const bounds = positionBounds(position);
  if (!bounds) {
    return null;
  }

  const spanDays = daysBetween(bounds.start.getTime(), bounds.end.getTime()) + 1;
  if (spanDays < 365) {
    return bounds;
  }

  return clampToCivilYear(bounds, today);
}

/** Fenêtre de pondération de `weightedWeeklyHours`, miroir de `contract_balance` côté back : année
 * civile de `today` écrêtée aux bornes de la position, systématiquement -- à la différence du
 * diviseur mensuel, une position courte n'élargit jamais la fenêtre à sa propre durée. */
function weightingWindowForWeeklyHours(
  position: ContractHoursPosition,
  today: Date,
): { start: Date; end: Date } | null {
  const bounds = positionBounds(position);
  if (!bounds) {
    return null;
  }

  return clampToCivilYear(bounds, today);
}

/** Somme jour par jour d'une valeur résolue sur une fenêtre, avec le nombre de jours retenus. Par
 * défaut (`countAllDays` faux), un jour sans valeur, `null`, n'entre ni au numérateur ni au
 * dénominateur -- la règle de `weightedMonthlyHours`. `weightedWeeklyHours` a besoin de l'inverse :
 * un jour dont l'entrée applicable n'a pas de `weekly_hours`/`weekly_days` exploitable, ou n'a pas
 * d'entrée du tout, compte quand même pour 0 heure au dénominateur, comme au back. */
function accumulateOverWindow(
  position: ContractHoursPosition,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
  window: { start: Date; end: Date } | null,
  extract: (resolved: ResolvedContractHours) => number | null,
  countAllDays = false,
): { sum: number; days: number } | null {
  if (!window) {
    return null;
  }

  let sum = 0;
  let days = 0;
  const day = new Date(window.start);
  while (day <= window.end) {
    const resolved = resolveContractHours(position, day, predefinedTimesById, { ignorePositionBounds: true });
    const value = resolved ? extract(resolved) : null;
    if (value !== null && value !== undefined) {
      sum += value;
      days += 1;
    } else if (countAllDays) {
      days += 1;
    }
    day.setDate(day.getDate() + 1);
  }

  return { sum, days };
}

/**
 * Diviseur du coût horaire : `monthly_hours` moyen pondéré de la position sur la fenêtre de
 * pondération. Miroir de `compute_weighted_average_monthly_hours` côté back.
 *
 * **Le front divise le coût mensuel par cette valeur pour afficher ET enregistrer le coût
 * horaire : la modifier déplace une valeur monétaire persistée.**
 */
export function weightedMonthlyHours(
  position: ContractHoursPosition,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
  today: Date = new Date(),
): number | null {
  const acc = accumulateOverWindow(position, predefinedTimesById, weightingWindow(position, today), (r) =>
    r.monthly_hours === null || r.monthly_hours === undefined ? null : r.monthly_hours,
  );
  if (!acc || acc.days <= 0) {
    return null;
  }

  return roundToTwoDecimals(acc.sum / acc.days);
}

/** Moyenne pondérée de `weekly_hours` sur l'année civile de `today` écrêtée aux bornes de la
 * position, non arrondie : sert de base au calcul du solde de vacances, qui a besoin de la
 * précision. Miroir de `contract_balance` côté back : contrairement à `weightedMonthlyHours`, la
 * fenêtre ne s'élargit jamais à la durée d'une position courte, et un jour dont l'entrée
 * applicable n'a pas de `weekly_hours`/`weekly_days` exploitable -- ou n'a pas d'entrée du tout --
 * compte quand même pour 0 heure au dénominateur. */
export function weightedWeeklyHours(
  position: ContractHoursPosition,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
  today: Date = new Date(),
): number | null {
  const acc = accumulateOverWindow(
    position,
    predefinedTimesById,
    weightingWindowForWeeklyHours(position, today),
    (r) => (r.weekly_hours && r.weekly_days ? r.weekly_hours : null),
    true,
  );
  if (!acc || acc.days <= 0) {
    return null;
  }

  return acc.sum / acc.days;
}

/** Heures dues d'une semaine entière, bornes de position ignorées comme les autres compteurs de
 * vue semaine (cf. `ResolveContractHoursOptions.ignorePositionBounds`). */
export function resolveWeekContractHours(
  position: ContractHoursPosition,
  weekStart: Date,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
): number {
  return resolveWeekPositionDailyHours(position, weekStart, predefinedTimesById, {
    ignorePositionBounds: true,
  }).workingHoursForDays.reduce((total, hours) => total + hours, 0);
}

/** Repli sur le champ plat quand la somme des sept jours vaut 0 : une position legacy peut porter
 * `weekly_hours` sans jamais avoir eu ses `daily_hours` renseignés, auquel cas la somme jour par
 * jour rendrait 0 alors que la position a bel et bien des heures dues. */
export function weekContractHoursWithFallback(
  position: ContractHoursPosition,
  weekStart: Date,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
): number {
  const resolved = resolveWeekContractHours(position, weekStart, predefinedTimesById);
  if (resolved > 0) {
    return resolved;
  }

  return (
    resolveContractHours(position, weekStart, predefinedTimesById, { ignorePositionBounds: true })?.weekly_hours ??
    position?.weekly_hours ??
    0
  );
}

/** Libellé « N jours/sem » des exports Excel. Repli sur le champ plat si aucune entrée ne couvre
 * le jour, pour ne jamais afficher `null`. */
export function weeklyDaysLabel(
  position: ContractHoursPosition,
  day: Date,
  predefinedTimesById: Map<string, PlanningPredefinedTimes>,
): string {
  const resolved = resolveContractHours(position, day, predefinedTimesById, { ignorePositionBounds: true });
  return (resolved?.weekly_days ?? position.weekly_days) + " jours/sem";
}

/** Seul le mode limité peut être inversé : une période récurrente aux bornes inversées chevauche
 * le nouvel an (15.11 -> 15.02) et reste valide. */
export function isPeriodInverted(period: PredefinedTimesPeriod): boolean {
  if (!period || period.mode !== PredefinedTimesPeriodMode.LIMITED || !period.start || !period.end) {
    return false;
  }
  return boundAtMidnight(period.start) > boundAtMidnight(period.end);
}

/** JS `getDay()` renvoie 0 = dimanche, `daily_hours` et `times.days` sont indexés 0 = lundi. */
function mondayBasedWeekDay(day: Date): number {
  return (day.getDay() + 6) % 7;
}

function defaultDailyHours(): HoursPerDay[] {
  return Array.from({ length: 7 }, (_, day) => ({ day, hours: 0, half_day: false }));
}

function createHoursPerDay(jsonData: HoursPerDay): HoursPerDay {
  return {
    day: jsonData?.day || 0,
    hours: jsonData?.hours || 0,
    half_day: jsonData?.half_day || false,
  };
}

/**
 * Reporte le jour/mois d'une borne sur l'année de référence non bissextile.
 *
 * Miroir de `month_day_on_year` côté back : une borne au 29.02 n'existe pas cette année-là, et
 * `new Date(2001, 1, 29)` déborde silencieusement au 1er mars. La durée rendue valait alors un
 * jour de plus que celle du back, et comme la durée départage les entrées applicables au même
 * jour, les deux moteurs pouvaient élire deux entrées différentes.
 */
function monthDayOnReferenceYear(bound: Date | string): number {
  const parts = zurichParts(bound);
  if (!parts) {
    return NaN;
  }
  const day = parts.month === 2 && parts.day === 29 ? 28 : parts.day;

  return new Date(REFERENCE_YEAR, parts.month - 1, day).getTime();
}

/**
 * Le 29.02 est traité comme le 28.02 : les bornes ne portent jamais le 29 (ramené au 28 à
 * l'enregistrement), et sans ce recalage le 29.02 collisionnerait avec le 01.03.
 */
function clampLeapDay(date: Date): Date {
  const clamped = new Date(date);
  if (clamped.getMonth() === 1 && clamped.getDate() === 29) {
    clamped.setDate(28);
  }

  return clamped;
}

function atMidnight(date: Date): number {
  return new Date(date).setHours(0, 0, 0, 0);
}

/** Jour interrogé : lu en heure locale de l'appareil, comme le back lit son argument `day`. */
function monthDayOrdinal(date: Date): number {
  return (date.getMonth() + 1) * 100 + date.getDate();
}

/**
 * Jour civil Europe/Zurich d'une borne de période, quel que soit le fuseau de l'appareil.
 *
 * Le back dérive le jour d'une borne ISO via `to_eva_local` (conversion Europe/Zurich) avant
 * d'en lire mois/jour ; lire une borne avec `Date.getDate()` la fait dépendre du fuseau du
 * navigateur, qui peut désigner une autre journée civile que celle voulue par l'utilisateur.
 */
/** Partagé entre tous les appels : `Intl.DateTimeFormat` est coûteux à instancier. */
const ZURICH_DATE_FORMAT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Zurich",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function zurichParts(bound: Date | string): { year: number; month: number; day: number } | null {
  const d = new Date(bound);
  if (Number.isNaN(d.getTime())) {
    // Miroir de l'ancien `atMidnight` : une borne malformée doit éteindre la comparaison, pas
    // faire planter `formatToParts`, qui lève sur une Invalid Date.
    return null;
  }
  const parts = ZURICH_DATE_FORMAT.formatToParts(d);
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);

  return { year: value("year"), month: value("month"), day: value("day") };
}

/** Minuit local du jour civil Europe/Zurich d'une borne, comparable à `atMidnight(day)`. */
export function boundAtMidnight(bound: Date | string): number {
  const parts = zurichParts(bound);
  if (!parts) {
    return NaN;
  }

  return new Date(parts.year, parts.month - 1, parts.day).setHours(0, 0, 0, 0);
}

export function boundMonthDayOrdinal(bound: Date | string): number {
  const parts = zurichParts(bound);

  return parts ? parts.month * 100 + parts.day : NaN;
}

/** Arrondi au jour entier : encaisse les heures d'écart d'un changement d'heure. */
function daysBetween(from: number, to: number): number {
  return Math.round((to - from) / MS_PER_DAY);
}

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

/** `round(x, 2)` de Python, qui départage les .xx5 exacts vers le pair et non vers le haut. Le
 * temps mensuel dérivé tombe dessus (20 h / 82.5 h mensuelles × une plage à 25 h = 103.125) et
 * `Math.round` rendrait 0.01 de plus que la valeur que le back resservira. */
function roundHalfToEvenTwoDecimals(value: number): number {
  const scaled = value * 100;
  const lower = Math.floor(scaled);
  const rest = scaled - lower;
  if (rest !== 0.5) {
    return Math.round(scaled) / 100;
  }

  return (lower % 2 === 0 ? lower : lower + 1) / 100;
}

function generateObjectId(): string {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

  return timestamp + randomHex;
}
